import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import _ from 'underscore';

import {CalendarProject} from 'calendar/CalendarConstants';
import DataConnector from 'connect/DataConnector';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import {BLUE, BLUE_LIGHT} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';

import CalendarActions from './CalendarActions';
import CalendarStore from './CalendarStore';
import CalendarUtil from './CalendarUtil';

class CalendarFilterConnector extends DataConnector<Props, EnhancedProps> {
  shouldSubscribeToRedux = false;
  stores = [CalendarStore];

  enhanceProps(): EnhancedProps {
    return {
      hiddenProjectIds: this.calendarStore().getHiddenProjectIds(),
      projects: this.calendarStore().getAllProjects(),
    };
  }
}

interface Props {}

interface EnhancedProps {
  hiddenProjectIds: Array<string>;
  projects: Array<CalendarProject>;
}

class CalendarFilter extends React.Component<Props & EnhancedProps> {
  _handleToggleProject = (projectId: string): void => {
    CalendarActions.toggleProjectHidden(projectId);
  };

  render(): JSX.Element {
    const projects = _.sortBy(this.props.projects, (project) => project.name.toLowerCase());
    const visibleProjects = projects.filter((project) => !this.props.hiddenProjectIds.includes(project.id));

    if (projects.length === 0) {
      return <div>No activity was found in this date range.</div>;
    }

    const projectColorMapping = CalendarUtil.getProjectColorMapping(this.props.projects);

    return (
      <div className="calendarFilter u-small-spacing-top">
        {projects.map((project, i) => {
          if (this.props.hiddenProjectIds.indexOf(project.id) !== -1) {
            return null;
          }
          const tooltipOptions = {
            title: project.name,
            placement: TooltipPlacement.BOTTOM,
            trigger: 'hover',
            container: 'body',
          };
          return (
            <Tooltip {...tooltipOptions} key={i}>
              <div
                className={css(styles.swatch)}
                style={{backgroundColor: projectColorMapping[project.id]}}
                {...getTestProps({component: 'CalendarFilter', element: 'swatch'})}
              />
            </Tooltip>
          );
        })}
        <Dropdown
          closeOnInsideClick={false}
          menu={
            <Menu value={visibleProjects} height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.LARGE}>
              {projects.map((project, i) => {
                const projectColor = projectColorMapping[project.id];
                return (
                  <Option key={i} value={project.id} onSelect={this._handleToggleProject}>
                    <div
                      className={css(styles.swatch)}
                      style={{
                        backgroundColor:
                          this.props.hiddenProjectIds.indexOf(project.id) !== -1 ? undefined : projectColor,
                        border: `solid 1px ${projectColor}`,
                      }}
                    />
                    {project.name}
                  </Option>
                );
              })}
            </Menu>
          }
        >
          <span className={css(styles.button)}>
            {'Filter '}
            <BenchlingIcon icon="chevron-down" />
          </span>
        </Dropdown>
      </div>
    );
  }
}

const styles = StyleSheet.create({
  button: {
    color: BLUE_LIGHT,
    cursor: 'pointer',
    ':hover': {
      color: BLUE,
    },
  },
  swatch: {
    borderRadius: '50%',
    display: 'inline-block',
    height: '12px',
    marginRight: '4px',
    width: '12px',
  },
});

export default CalendarFilterConnector.connect(CalendarFilter);
